import { Action, Module, VuexModule } from 'vuex-class-modules'
import auth from '@/store/modules/auth'
import company from '@/services/company'
import credentials from '@/services/credentials'
import vehicles from '@/services/vehicle'
import tracking from '@/services/tracking'
import store from '..'
import { TrackingSource, VehicleTrackingIndicator } from '@/models/dto/Tracking'
import { VINReview } from '@/models/dto/Vehicle'
import { TrackingIndicator } from '@/utils/enum'
import axios, { AxiosError } from 'axios'

@Module({ generateMutationSetters: true })
class CompanyModule extends VuexModule {
  _hasEldCredentials = false
  _eldTypes: string[] = []
  _eldIndicators: VehicleTrackingIndicator[] = null
  _allVehiceIds: number[] = null
  _trackingSources: TrackingSource[] = []
  _vinReviews: VINReview[] = []
  _hasBusifyAvailabilityIntegrationEnabled = false
  _vehicleIdsRequestPromise: Promise<Array<number>> = null
  _trackingIndicatorsRequestPromise: Promise<Array<VehicleTrackingIndicator>> =
    null

  get hasEldCredentials(): boolean {
    return this._hasEldCredentials
  }

  get eldTypes(): string[] {
    return this._eldTypes
  }

  get trackingSources(): TrackingSource[] {
    return this._trackingSources
  }

  get vinReviews(): VINReview[] {
    return this._vinReviews
  }

  get hasBusifyAvailabilityIntegrationEnabled(): boolean {
    return this._hasBusifyAvailabilityIntegrationEnabled
  }

  @Action
  async getEldCredentials(): Promise<void> {
    const credentialsResponse = await credentials.getAllByCompanyIdV2(
      auth.user.companyId
    )
    const result = credentialsResponse?.data?.data
    this._hasEldCredentials = result?.length > 0
    const eldTypes = []
    for (const credential of result) {
      eldTypes.push(credential.eldType)
    }
    this._eldTypes = eldTypes
  }

  @Action
  async getTrackingSources(): Promise<void> {
    let trackingSourcesResponse
    try {
      trackingSourcesResponse = await company.getTrackingSources(
        auth.company.companyId
      )
    } catch (e) {
      console.error(e)
      return
    }
    this._trackingSources = trackingSourcesResponse?.data?.data
  }

  @Action
  async getVINReviews(): Promise<void> {
    const vinReviewsResponse = await company.getVINReviews()
    const vinReviews = vinReviewsResponse?.data?.vinReviews
    this._vinReviews = vinReviews.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )
  }

  @Action
  async getBusifyAvailabilityIntegrationEnabled(): Promise<void> {
    try {
      const response = await company.getBusifyIntegrationFeatures(
        auth.user.companyId
      )
      this._hasBusifyAvailabilityIntegrationEnabled =
        response?.data?.availability?.enabled
      return
    } catch (e: unknown | AxiosError) {
      if (axios.isAxiosError(e) && e?.response?.status === 404) {
        // A 404 is the expected response when the operator does not have SaaS integration
        console.info('Operator does not have SaaS integration.')
      } else {
        console.error("Error checking operator's SaaS integration:", e)
      }
    }

    this._hasBusifyAvailabilityIntegrationEnabled = false
  }

  @Action
  async getAllVehicleIds(): Promise<Array<number>> {
    // If we already have the data, return it immediately
    if (this._allVehiceIds) {
      return this._allVehiceIds
    }

    // If a request is already in progress, return the existing promise
    if (this._vehicleIdsRequestPromise) {
      return this._vehicleIdsRequestPromise
    }

    // Start a new request and store the promise
    this._vehicleIdsRequestPromise = (async () => {
      try {
        const response = await vehicles.allVehicles()
        this._allVehiceIds = response.data.resultList.map(
          (vehicle) => vehicle.vehicleId
        )
        return this._allVehiceIds
      } catch (error) {
        console.error('Error fetching vehicle IDs:', error)
        throw error
      } finally {
        // Clear the promise reference when done
        this._vehicleIdsRequestPromise = null
      }
    })()

    return this._vehicleIdsRequestPromise
  }

  @Action
  async getAllTrackingIndicators(): Promise<Array<VehicleTrackingIndicator>> {
    // If we already have the data, return it immediately
    if (this._eldIndicators) {
      return this._eldIndicators
    }

    // If a request is already in progress, return the existing promise
    if (this._trackingIndicatorsRequestPromise) {
      return this._trackingIndicatorsRequestPromise
    }

    // Start a new request and store the promise
    this._trackingIndicatorsRequestPromise = (async () => {
      try {
        const allVehicleIds = await this.getAllVehicleIds()
        const response = await tracking.vehicleIndicators(allVehicleIds)
        this._eldIndicators = response.data.vehicles
        return this._eldIndicators
      } catch (error) {
        console.error('Error fetching tracking indicators:', error)
        throw error
      } finally {
        // Clear the promise reference when done
        this._trackingIndicatorsRequestPromise = null
      }
    })()

    return this._trackingIndicatorsRequestPromise
  }

  @Action
  async getTrackingIndicator(
    vehicleId: number
  ): Promise<VehicleTrackingIndicator> {
    const allIndicators = await this.getAllTrackingIndicators()
    return allIndicators.find((indicator) => indicator.vehicleId === vehicleId)
  }

  @Action
  async getBadTrackingIndicators(): Promise<Array<VehicleTrackingIndicator>> {
    const allIndicators = await this.getAllTrackingIndicators()
    return allIndicators.filter(
      (indicator) =>
        indicator.indicator === TrackingIndicator.RED ||
        indicator.indicator === TrackingIndicator.YELLOW
    )
  }
}

export default new CompanyModule({ store, name: 'company' })
